/**
 * New bill (S15/S16) — upload/extract and draft review.
 *
 * Supports `?source=upload|email|manual` deep-link so callers can open the
 * composer with a specific capture mode pre-selected.
 *
 * @see prototype/purchases/bill-new.html — V1.1 baseline (S15/S16)
 * @see flowtrail/erp ▸ docs/internal/product/lucid-v1-demo-scope.md §3.3 — vendor bill OCR upload + review
 */
import { createFileRoute } from "@tanstack/react-router";
import { WorkspaceShell } from "../../../../features/workspace/index.js";
import { BillComposerPage } from "../../../../features/bills/new/index.js";
import type { BillSourceMode } from "../../../../features/bills/new/lib/form-schema.js";

const SOURCE_MODES: ReadonlyArray<BillSourceMode> = ["upload", "email", "manual"];

interface NewBillSearch {
  source?: BillSourceMode;
}

export const Route = createFileRoute("/(workspace)/procurement/bills/new")({
  component: NewBillRoute,
  validateSearch: (s: Record<string, unknown>): NewBillSearch => {
    const out: NewBillSearch = {};
    if (typeof s.source === "string" && (SOURCE_MODES as readonly string[]).includes(s.source)) {
      out.source = s.source as BillSourceMode;
    }
    return out;
  },
});

function NewBillRoute() {
  const search = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Procurement", to: "/" },
        { label: "Bills", to: "/procurement/bills" },
        { label: "New" },
      ]}
      contentClassName="max-w-none"
      // Kill default ScrollArea viewport padding at *both* breakpoints. The
      // default is `p-5 md:p-6`; `p-0` alone only overrides the base — the
      // responsive `md:p-6` still wins ≥ md, leaving a 24px gutter above
      // the sticky composer-head that visually wraps it in a card.
      viewportClassName="p-0 md:p-0"
    >
      <BillComposerPage {...(search.source ? { initialSourceMode: search.source } : {})} />
    </WorkspaceShell>
  );
}
